import { Drawer, TextInput } from "@caprikorn/dynamo-ui";
import { type FC, useId } from "react";
import { Controller, useForm } from "react-hook-form";

import { FORM_VALIDATION_MODE } from "@core/core.constants";
import { useGetCustomerContract } from "@services/apiEcms/useGetCustomerContract";
import { usePatchCustomerContract } from "@services/apiEcms/usePatchCustomerContract";
import { ADDRESSING_CODE_REGEX } from "@utils/form";
import { useCustomTranslation } from "@utils/translations/useCustomTranslation";

import classes from "./AddressingCodeDrawer.module.css";
import type {
  AddressingCodeDrawerProps,
  AddressingCodeForm,
} from "./AddressingCodeDrawer.types";

const ADDRESSING_CODE_MAX_LENGTH = 125;

export const AddressingCodeDrawer: FC<AddressingCodeDrawerProps> = ({
  customerId,
  contractId,
  onClose,
}) => {
  const { t } = useCustomTranslation(["main", "common"]);
  const formId = useId();

  const { data: contract } = useGetCustomerContract({ customerId, contractId });

  const {
    mutate: updateAddressingCode,
    isPending: isAddressingCodeUpdatePending,
  } = usePatchCustomerContract();

  const {
    control,
    handleSubmit,
    formState: { isSubmitted },
  } = useForm<AddressingCodeForm>({
    mode: FORM_VALIDATION_MODE,
    defaultValues: {
      addressingCode: contract?.contractInformation.addressingCode ?? "",
    },
  });

  const onSubmit = (data: AddressingCodeForm) => {
    updateAddressingCode(
      {
        customerId,
        contractId,
        addressingCode: data.addressingCode.trim(),
      },
      { onSuccess: onClose },
    );
  };

  return (
    <Drawer onClose={onClose}>
      <Drawer.Header
        title={t("perimeter.overview.site.eInvoicing.drawer.title")}
      />
      <Drawer.Body>
        <form
          className={classes.form}
          id={formId}
          onSubmit={handleSubmit(onSubmit)}
        >
          <Controller
            control={control}
            name="addressingCode"
            render={({ field, fieldState }) => {
              const trimmedValue = field.value.trim();
              const isFormatWarningVisible =
                (fieldState.isTouched || isSubmitted) &&
                trimmedValue !== "" &&
                !ADDRESSING_CODE_REGEX.test(trimmedValue);

              const getHint = () => {
                if (fieldState.error?.message) {
                  return t(fieldState.error.message, { ns: "common" });
                }
                if (isFormatWarningVisible) {
                  return t(
                    "perimeter.overview.site.eInvoicing.drawer.formatWarning",
                  );
                }
                return undefined;
              };

              return (
                <TextInput
                  className={classes.input}
                  hasError={Boolean(fieldState.error)}
                  hint={getHint()}
                  label={t("perimeter.overview.site.eInvoicing.drawer.label")}
                  maxLength={ADDRESSING_CODE_MAX_LENGTH}
                  size="lg"
                  {...field}
                />
              );
            }}
            rules={{
              validate: (value) => value.trim() !== "" || "form.error.required",
            }}
          />
        </form>
      </Drawer.Body>
      <Drawer.Footer>
        <Drawer.Footer.ConfirmButton
          form={formId}
          isLoading={isAddressingCodeUpdatePending}
          type="submit"
        />
        <Drawer.Footer.CancelButton />
      </Drawer.Footer>
    </Drawer>
  );
};
